import {
  Alert,
  AlertIcon,
  Badge,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import TermosApi, {
  TermoPendencia,
  TermosPendencias,
} from "../../services/termosApi";
import LeitorTermo from "./components/leitorTermo";
import { GeoCaptura, obterGeolocalizacao } from "./geolocalizacao";
import BloqueioTermo from "./components/bloqueioTermo";

/**
 * Porta de entrada do portal: se houver termo pendente, ele aparece antes de qualquer
 * outra tela.
 *
 * Regras que vêm do backend, não daqui:
 * - só o usuário master aceita (podeAceitar); o leitor lê e fecha;
 * - termo bloqueante não pode ser dispensado por quem pode aceitar;
 * - recusar só bloqueia (e só desloga) quando o termo era bloqueante; termo de ciência
 *   segue o fluxo normal depois da recusa.
 */
export default function TermosGate() {
  const [dados, setDados] = useState<TermosPendencias | null>(null);
  const [indice, setIndice] = useState(0);
  const [leuAteFim, setLeuAteFim] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [dispensado, setDispensado] = useState(false);
  const [recusou, setRecusou] = useState(false);
  const [geo, setGeo] = useState<GeoCaptura | null>(null);
  const [geoPedida, setGeoPedida] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const resposta = await TermosApi.getPendencias();
      setDados(resposta);
      setIndice(0);
      setLeuAteFim(false);
    } catch (error) {
      // pendência de termo não pode derrubar o portal inteiro
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Pede a posição assim que há termo na tela: o prompt do navegador aparece enquanto a
  // pessoa lê, e a resposta já está pronta na hora de decidir. Negar não impede nada.
  useEffect(() => {
    if (geoPedida) return;
    if (!dados) return;
    if (dados.bloqueantes.length === 0 && dados.avisos.length === 0) return;

    setGeoPedida(true);
    obterGeolocalizacao().then(setGeo);
  }, [dados, geoPedida]);

  const pendencias: TermoPendencia[] = dados
    ? [...dados.bloqueantes, ...dados.avisos]
    : [];

  const termo = pendencias[indice];

  // Acabou de recusar: a mensagem fica na tela e o logout vem em seguida.
  if (recusou)
    return (
      <BloqueioTermo
        detalhe="A recusa foi registrada. Para voltar a usar o portal, a central precisa reabrir o termo ou emitir um novo para o seu estabelecimento."
        autoSairEm={15}
      />
    );

  // Bloqueado: recusou ou deixou o prazo vencer. Não há mais o que aceitar — a saída é
  // pela central, que prorroga ou reabre o aviso. O modal não fecha e não tem ação.
  if (dados?.bloqueado) return <BloqueioTermo mensagem={dados.mensagemBloqueio} />;

  if (!dados || !termo || dispensado) return <></>;

  const podeAceitar = dados.podeAceitar;
  const bloqueante = termo.bloqueante;
  // termo bloqueante para quem pode resolver: não há como sair sem decidir
  const podeFechar = !bloqueante || !podeAceitar;

  function proximo() {
    setLeuAteFim(false);
    setMotivo("");
    setRecusando(false);

    if (indice + 1 < pendencias.length) {
      setIndice(indice + 1);
    } else {
      carregar();
    }
  }

  async function aceitar() {
    setEnviando(true);
    try {
      await TermosApi.registrarExibicao(termo.idTermVersion, true);
      await TermosApi.aceitar(termo.idTermVersion, termo.tokenExibicao, geo ?? undefined);
      toast.success("Termo aceito. Obrigado!");
      proximo();
    } catch (error: any) {
      toast.error(
        error?.response?.data || "Não foi possível registrar o aceite."
      );
    } finally {
      setEnviando(false);
    }
  }

  async function recusar() {
    if (motivo.trim().length < 5) {
      toast.warning("Descreva o motivo da recusa.");
      return;
    }

    setEnviando(true);
    try {
      const resposta = await TermosApi.recusar(
        termo.idTermVersion,
        termo.tokenExibicao,
        motivo.trim(),
        geo ?? undefined
      );

      // Só vira tela de bloqueio quando a recusa BLOQUEOU de fato. Termo publicado sem
      // "bloquear o portal" é ciência: recusar não tira o acesso, e deslogar o cliente
      // seria punição por um efeito que não existe.
      if (resposta.bloqueado) {
        // toast desaparece e o cliente não entende por que caiu; a mensagem fica na tela
        setRecusou(true);
        return;
      }

      toast.info("Recusa registrada.");
      proximo();
    } catch (error: any) {
      toast.error(
        error?.response?.data || "Não foi possível registrar a recusa."
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => setDispensado(true)}
      closeOnOverlayClick={false}
      closeOnEsc={podeFechar}
      size="4xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb="1">
          <Flex justifyContent="space-between" alignItems="center" gap="3">
            <Text fontSize="lg">{termo.titulo}</Text>
            <Flex gap="2" alignItems="center">
              <Badge colorScheme="gray">versão {termo.versao}</Badge>
              {termo.vencido ? (
                <Badge colorScheme="red">prazo vencido</Badge>
              ) : (
                <Badge colorScheme={termo.diasRestantes <= 3 ? "orange" : "blue"}>
                  {termo.diasRestantes} dia(s) para aceitar
                </Badge>
              )}
              {pendencias.length > 1 && (
                <Badge colorScheme="purple">
                  {indice + 1} de {pendencias.length}
                </Badge>
              )}
            </Flex>
          </Flex>
          {termo.resumo && (
            <Text fontSize="sm" color="gray.600" fontWeight="normal" mt="2">
              {termo.resumo}
            </Text>
          )}
        </ModalHeader>

        <ModalBody>
          {!podeAceitar && (
            <Alert status="info" mb="3" borderRadius="6px">
              <AlertIcon />
              {dados.motivoBloqueioAceite ||
                "Somente o usuário master do estabelecimento pode aceitar este termo."}
            </Alert>
          )}

          {termo.erroDocumento && (
            <Alert status="error" mb="3" borderRadius="6px">
              <AlertIcon />
              {termo.erroDocumento} Acione a central da Monedd.
            </Alert>
          )}

          {geoPedida && !geo && podeAceitar && (
            <Alert status="info" mb="3" borderRadius="6px" fontSize="sm">
              <AlertIcon />
              Não conseguimos registrar a sua localização. O aceite continua valendo — o
              registro apenas fica sem esse dado.
            </Alert>
          )}

          {termo.html && (
            <LeitorTermo
              html={termo.html}
              onFimDaLeitura={() => setLeuAteFim(true)}
            />
          )}

          {recusando && (
            <Flex direction="column" mt="3" gap="2">
              <Alert status="warning" borderRadius="6px">
                <AlertIcon />
                {bloqueante
                  ? "Recusar bloqueia o acesso ao portal até que a central da Monedd trate o caso."
                  : "A recusa fica registrada. Este termo é de ciência, então o seu acesso ao portal continua normal."}
              </Alert>
              <Textarea
                placeholder="Motivo da recusa"
                value={motivo}
                maxLength={1000}
                onChange={(e) => setMotivo(e.target.value)}
              />
            </Flex>
          )}
        </ModalBody>

        <ModalFooter gap="2">
          {podeFechar && !recusando && (
            <Button variant="ghost" onClick={() => setDispensado(true)}>
              Ler depois
            </Button>
          )}

          {podeAceitar && !recusando && (
            <Button
              colorScheme="red"
              variant="outline"
              isDisabled={enviando}
              onClick={() => setRecusando(true)}
            >
              Recusar
            </Button>
          )}

          {recusando && (
            <>
              <Button variant="ghost" onClick={() => setRecusando(false)}>
                Voltar
              </Button>
              <Button
                colorScheme="red"
                isLoading={enviando}
                onClick={recusar}
              >
                Confirmar recusa
              </Button>
            </>
          )}

          {podeAceitar && !recusando && (
            <Button
              colorScheme="blue"
              isLoading={enviando}
              isDisabled={!leuAteFim || !!termo.erroDocumento}
              onClick={aceitar}
            >
              {leuAteFim ? "Li e aceito" : "Role até o fim para aceitar"}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
